import { format, parseISO } from 'date-fns';
import { Camera } from 'lucide-react';
import { Link } from 'react-router-dom';

import noLogEntriesPng from '@/assets/illustrations/empty/no-log-entries-surface.png';

import type { LogEntry } from '@/types/log-entry';
import { formatAnimalSummary } from '@/utils/scouting-utils';
import { OperationalGuide } from '@/components/common/OperationalGuide';

interface RecentLogSectionProps {
  entries: LogEntry[];
  onEntryClick: (id: string) => void;
  onAddClick: () => void;
  /** Optional property name lookup map (propertyId → name) for location context */
  propertyNames?: Record<string, string>;
  /** Optional waypoint name lookup map (waypointId → name) for location context */
  waypointNames?: Record<string, string>;
  /**
   * 'standalone': full card with own border and rounded-lg (default)
   * 'seated': no top border/radius — CollapsibleWidget header provides the top edge
   */
  variant?: 'standalone' | 'seated';
}

const MAX_ENTRIES = 2;

export function RecentLogSection({
  entries,
  onEntryClick,
  onAddClick,
  propertyNames,
  waypointNames,
  variant = 'standalone',
}: RecentLogSectionProps) {
  const sorted = [...entries]
    .sort((a, b) => {
      const dateCmp = b.date.localeCompare(a.date);
      if (dateCmp !== 0) return dateCmp;
      if (a.time && b.time) return b.time.localeCompare(a.time);
      return 0;
    })
    .slice(0, MAX_ENTRIES);

  const count = entries.length;
  const headerLabel = count > 0 ? `LOG · ${count} RECENT ENTRIES` : 'LOG';

  // Seated variant: no top border/radius — CollapsibleWidget header provides the top edge
  const containerClass =
    variant === 'seated'
      ? 'bg-[var(--color-surface)] border-x border-b border-[var(--color-border)] rounded-b-lg rounded-t-none overflow-hidden'
      : 'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden';

  return (
    <div
      className={containerClass}
      data-testid="recent-log-section"
      data-variant={variant}
    >
      {/* Header — stays in body regardless of variant (+ LOG button must NOT move to CollapsibleWidget
          titleExtra, as that would nest a button inside a button, violating HTML spec) */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
        <span
          className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]"
          data-testid="recent-log-header"
        >
          {headerLabel}
        </span>
        <button
          type="button"
          onClick={onAddClick}
          className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
          data-testid="add-entry-button"
        >
          + LOG
        </button>
      </div>

      {/* Empty state */}
      {sorted.length === 0 ? (
        <OperationalGuide
          illustration={noLogEntriesPng}
          heading="No Recent Scouts"
          subtext="Log your first scouting entry"
          ctaLabel="Log Entry"
          onCtaClick={onAddClick}
          compact
          data-guide-id="recent-log-empty"
        />
      ) : (
        <>
          {/* Entry list */}
          <div className="divide-y divide-[var(--color-border)]">
            {sorted.map((entry) => {
              const animalSummary = formatAnimalSummary(entry);
              const primaryDetail =
                animalSummary ??
                (entry.notes.trim()
                  ? entry.notes.trim().slice(0, 80) + (entry.notes.length > 80 ? '…' : '')
                  : null);

              const propertyName = entry.propertyId
                ? propertyNames?.[entry.propertyId]
                : undefined;
              const waypointName = entry.waypointId
                ? waypointNames?.[entry.waypointId]
                : undefined;
              const locationContext = [propertyName, waypointName]
                .filter(Boolean)
                .join(' · ');

              // Key stat: photo count if has photos; wind direction if available
              const photoCount = entry.photos?.length ?? 0;
              const hasKeyStat = photoCount > 0 || Boolean(entry.windDirection);

              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => onEntryClick(entry.id)}
                  className="w-full text-left px-4 py-3 flex flex-col gap-0.5 hover:bg-[var(--color-elevated)] transition-colors"
                  data-testid={`entry-card-${entry.id}`}
                >
                  {/* Date / Time row */}
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                    {format(parseISO(entry.date), 'MMM d').toUpperCase()}
                    {entry.time && `, ${entry.time}`}
                  </p>

                  {/* Primary detail */}
                  {primaryDetail && (
                    <p className="text-[14px] font-medium leading-snug text-[var(--color-text-primary)]">
                      {primaryDetail}
                    </p>
                  )}

                  {/* Location context + key stat row */}
                  <div className="flex items-center justify-between gap-2">
                    {locationContext && (
                      <p className="text-[12px] text-[var(--color-text-muted)] truncate">
                        {locationContext}
                      </p>
                    )}
                    {hasKeyStat && (
                      <div
                        className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] shrink-0 ml-auto"
                        data-testid={`entry-key-stat-${entry.id}`}
                      >
                        {photoCount > 0 ? (
                          <>
                            <Camera size={11} aria-hidden />
                            <span>{photoCount}</span>
                          </>
                        ) : (
                          <span>{entry.windDirection}</span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* View all link */}
          <div className="px-4 py-2 border-t border-[var(--color-border)] rounded-b-lg">
            <Link
              to="/scout/log"
              className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
              data-testid="view-all-scouting"
            >
              View All Scouts →
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
